import {
  createBusinessPartnerRequestSchema,
  masterDataDuplicateDetailsSchema,
  type CreateBusinessPartnerRequest,
  type MasterDataDuplicateCandidate,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useCreateBusinessPartner } from '@/platform/features/master-data/use-master-data';
import { masterDataPartnerPath } from '@/platform/lib/master-data-paths';
import { useVoiceCopy } from '@/platform/voice/use-voice';

export function PartnerFormPage() {
  const copy = useVoiceCopy();
  const navigate = useNavigate();
  const createPartner = useCreateBusinessPartner();
  const [candidates, setCandidates] = useState<MasterDataDuplicateCandidate[]>([]);
  const form = useForm<CreateBusinessPartnerRequest>({
    resolver: zodResolver(createBusinessPartnerRequestSchema),
    defaultValues: { legalName: '', validFrom: '' },
  });

  async function onValid(values: CreateBusinessPartnerRequest) {
    setCandidates([]);
    try {
      const created = await createPartner.mutateAsync(values);
      toast.success('Geschäftspartner angelegt.');
      void navigate(masterDataPartnerPath(created.id));
    } catch (error) {
      const found = duplicateCandidates(error);
      if (found) {
        setCandidates(found);
        return;
      }
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Geschäftspartner konnte nicht angelegt werden.',
      );
    }
  }

  async function confirmDuplicate() {
    const valid = await form.trigger();
    if (!valid) {
      return;
    }
    try {
      const created = await createPartner.mutateAsync({
        ...form.getValues(),
        acknowledgeDuplicate: true,
      });
      toast.success('Geschäftspartner angelegt.');
      void navigate(masterDataPartnerPath(created.id));
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Geschäftspartner konnte nicht angelegt werden.',
      );
    }
  }

  const nameError = form.formState.errors.legalName;
  const dateError = form.formState.errors.validFrom;

  return (
    <section className="max-w-xl space-y-6">
      <PlatformPageHeader
        title="Geschäftspartner anlegen"
        description="Die Lieferantenrolle wird mit dem Beginn des ersten Gültigkeitstags gesetzt."
      />
      <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
        <div className="space-y-2">
          <Label htmlFor="legalName">Rechtlicher Name</Label>
          <Input
            id="legalName"
            {...form.register('legalName')}
            aria-invalid={nameError ? true : undefined}
          />
          {nameError ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.legal-name')}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="tradeName">Handelsname</Label>
          <Input
            id="tradeName"
            {...form.register('tradeName', {
              setValueAs: (value: string) => (value.trim() === '' ? null : value),
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="validFrom">Gültig ab</Label>
          <Input
            id="validFrom"
            placeholder="JJJJ-MM-TT"
            {...form.register('validFrom')}
            aria-invalid={dateError ? true : undefined}
          />
          {dateError ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.date')}
            </p>
          ) : null}
        </div>
        <Button type="submit" disabled={createPartner.isPending}>
          Anlegen
        </Button>
        {candidates.length > 0 ? (
          <div role="alert" className="space-y-3 rounded-md border p-3 text-sm">
            <p>Ein Geschäftspartner mit diesem rechtlichen Namen ist schon vorhanden.</p>
            <ul className="space-y-1">
              {candidates.map((candidate) => (
                <li key={candidate.id}>
                  <Link className="underline" to={masterDataPartnerPath(candidate.id)}>
                    {candidate.partnerNumber}
                  </Link>
                  {` · ${candidate.legalName}`}
                </li>
              ))}
            </ul>
            <Button type="button" disabled={createPartner.isPending} onClick={confirmDuplicate}>
              Trotzdem anlegen
            </Button>
          </div>
        ) : null}
      </form>
    </section>
  );
}

function duplicateCandidates(error: unknown): MasterDataDuplicateCandidate[] | null {
  if (!(error instanceof ApiRequestError) || error.error.code !== 'MASTER_DATA_DUPLICATE') {
    return null;
  }
  const parsed = masterDataDuplicateDetailsSchema.safeParse(error.error.details);
  return parsed.success && parsed.data.candidates.length > 0 ? parsed.data.candidates : null;
}
